<template>
  <div
    class="whatsapp-onboarding-wizard"
    :class="{ 'whatsapp-onboarding-wizard--wide': wizard.currentStepId.value === 'number_choice' }"
  >
    <WhatsappOnboardingProgress
      :step-number="wizard.stepNumber.value"
      :total-steps="wizard.totalSteps.value"
    />
    <div class="whatsapp-onboarding-wizard__body">
      <IntroStep v-if="wizard.currentStepId.value === 'intro'" />
      <SituationStep
        v-else-if="wizard.currentStepId.value === 'situation'"
        :model-value="wizard.path.value"
        @update:model-value="onPathSelected"
      />
      <NumberChoiceStep
        v-else-if="wizard.currentStepId.value === 'number_choice'"
      />
      <AccessChecklistStep
        v-else-if="wizard.currentStepId.value === 'access_checklist'"
        :variant="wizard.path.value === 'none' ? 'standard' : 'other_bsp'"
        :show-display-name="wizard.path.value === 'none'"
      />
      <MetaTipsStep
        v-else-if="wizard.currentStepId.value === 'meta_tips'"
      />
      <MigrationPrepStep
        v-else-if="wizard.currentStepId.value === 'migration_prep'"
        @want-new-number="wizard.switchToNewNumberPath"
      />
      <MigrationWarningStep
        v-else-if="wizard.currentStepId.value === 'migration_warning'"
        variant="other_bsp"
        @want-new-number="wizard.switchToNewNumberPath"
      />
    </div>
    <WhatsappOnboardingNav
      :show-back="!wizard.isFirstStep.value"
      :show-next="showNextButton"
      :show-connect="wizard.isConnectStep.value"
      :can-go-next="wizard.canGoNext.value"
      :connect-loading="connectLoading"
      :is-intro-step="wizard.isIntroStep.value"
      @back="wizard.goBack"
      @next="handleNext"
      @connect="$emit('connect')"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';
import { useWhatsappOnboardingWizard } from '@/composables/whatsapp/useWhatsappOnboardingWizard';
import type { OnboardingPath } from '@/types/whatsapp-onboarding';
import WhatsappOnboardingNav from './WhatsappOnboardingNav.vue';
import WhatsappOnboardingProgress from './WhatsappOnboardingProgress.vue';
import IntroStep from './steps/IntroStep.vue';
import SituationStep from './steps/SituationStep.vue';
import NumberChoiceStep from './steps/NumberChoiceStep.vue';
import AccessChecklistStep from './steps/AccessChecklistStep.vue';
import MetaTipsStep from './steps/MetaTipsStep.vue';
import MigrationPrepStep from './steps/MigrationPrepStep.vue';
import MigrationWarningStep from './steps/MigrationWarningStep.vue';

export default defineComponent({
  name: 'WhatsappOnboardingWizard',

  components: {
    WhatsappOnboardingNav,
    WhatsappOnboardingProgress,
    IntroStep,
    SituationStep,
    NumberChoiceStep,
    AccessChecklistStep,
    MetaTipsStep,
    MigrationPrepStep,
    MigrationWarningStep,
  },

  props: {
    shopId: {
      type: Number,
      required: true,
    },
    connectLoading: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['connect'],

  setup(props) {
    const wizard = useWhatsappOnboardingWizard(props.shopId);

    const showNextButton = computed(() => (
      wizard.isIntroStep.value
      || (!wizard.isConnectStep.value && !wizard.isLastStep.value)
    ));

    const onPathSelected = (value: OnboardingPath) => {
      wizard.setPath(value);
    };

    const handleNext = () => {
      if (wizard.isIntroStep.value) {
        wizard.goNext();
        return;
      }
      wizard.goNext();
    };

    return {
      wizard,
      showNextButton,
      onPathSelected,
      handleNext,
    };
  },
});
</script>

<style lang="scss">
@import './whatsapp-onboarding.scss';
</style>
